import { TJob } from "apiTypes/jobs";
import MolhamButton from "components/ReuseableCompos/MolhamButton";
import useTranslation from "next-translate/useTranslation";
import { useRouter } from "next/router";
import React, { memo } from "react";
import styled from "styled-components";
import { pickLocale } from "./jobLocale";

type Props = {
  data?: TJob;
  skeleton?: boolean;
};

const JobCard = ({ data, skeleton }: Props) => {
  const { t } = useTranslation("common");
  const { locale } = useRouter();

  if (skeleton) {
    return (
      <JobCardWrapper className="mb-grid-gutter">
        <div className="card border h-100">
          <div className="card-body">
            <div
              className="placeholder-glow"
              style={{
                backgroundColor: "#e0e0e0",
                height: "24px",
                marginBottom: "12px",
              }}
            />
            <div
              className="placeholder-glow"
              style={{
                backgroundColor: "#e0e0e0",
                height: "20px",
                marginBottom: "12px",
              }}
            />
            <div
              className="placeholder-glow"
              style={{ backgroundColor: "#e0e0e0", height: "40px" }}
            />
          </div>
        </div>
      </JobCardWrapper>
    );
  }

  if (!data) return null;

  const { id, title, description, location, deadline, urgent_hiring_note } = data;

  const isExpired = deadline ? new Date(deadline).getTime() < Date.now() : false;
  const isUrgent = !!pickLocale(urgent_hiring_note, locale);
  const descriptionText = pickLocale(description, locale);

  // Format deadline for display: extract the date part only ("YYYY-MM-DD").
  const deadlineDisplay = deadline ? deadline.split(" ")[0] : null;

  return (
    <JobCardWrapper className="mb-grid-gutter">
      <div className="card border card-hover h-100">
        <div className="card-body d-flex flex-column">
          {/* Badges */}
          <div className="d-flex gap-2 align-items-center flex-wrap mb-2">
            <StatusBadge status={isExpired ? "closed" : "open"}>
              {isExpired ? t("closed") : t("active")}
            </StatusBadge>
            {!isExpired && isUrgent && <UrgentBadge>{t("urgent")}</UrgentBadge>}
          </div>

          {/* Title */}
          <JobTitle className="mb-2">{pickLocale(title, locale)}</JobTitle>

          {/* Description snippet */}
          {descriptionText && <Description>{descriptionText}</Description>}
          <div
            style={{
              borderTop: "1px solid #7f7f7f45",
              margin: "0.5rem 0",
            }}
          />
          {/* Meta */}
          <MetaRow className="mt-2">
            {location && (
              <MetaItem>
                <i className="fe-map-pin me-1" />
                {location}
              </MetaItem>
            )}
            {deadlineDisplay && (
              <MetaItem>
                <i className="fe-calendar me-1" />
                {t("deadline")}: <strong>{deadlineDisplay}</strong>
              </MetaItem>
            )}
          </MetaRow>

          {/* CTA */}
          <div className="mt-auto pt-3 d-flex justify-content-end">
            <MolhamButton
              id={`view-job-${id}`}
              href={`/jobs/${id}`}
              label={t("apply_now")}
              endIcon="fe-chevron-right"
              customHeight="40px"
              type="button"
            />
          </div>
        </div>
      </div>
    </JobCardWrapper>
  );
};

export default memo(JobCard);

// Styled Components
const JobCardWrapper = styled.div`
  width: 100%;

  .card-hover {
    transition: all 0.3s ease;
    &:hover {
      transform: translateY(-4px);
      box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
    }
  }
`;

const StatusBadge = styled.span<{ status: string }>`
  padding: 0.25rem 0.5rem;
  border-radius: 1rem;
  font-size: 0.7rem;
  font-weight: 600;
  text-transform: uppercase;
  background-color: ${({ status }) =>
    status === "open" ? "#436e4e" : "#b34852"};
  color: white;
`;

const UrgentBadge = styled.span`
  padding: 0.25rem 0.75rem;
  border-radius: 1rem;
  font-size: 0.75rem;
  font-weight: 600;
  text-transform: uppercase;
  background-color: #fff3cd;
  color: #856404;
`;

const JobTitle = styled.h5`
  font-size: 1.1rem;
  font-weight: 600;
  line-height: 1.6;
  color: var(--bs-heading-color);
  margin: 0;

  @media (max-width: 768px) {
    font-size: 1.1rem;
  }
`;

const MetaRow = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 0.5rem 1.25rem;
`;

const MetaItem = styled.div`
  display: flex;
  align-items: center;
  font-size: 0.9rem;
  color: var(--bs-secondary-color);
`;

const Description = styled.p`
  font-size: 0.9rem;
  line-height: 1.6;
  color: var(--bs-secondary-color);
  margin: 0;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
`;
